import { useState, useEffect } from 'react';
import {
    Box, Heading, Table, Thead, Tbody, Tr, Th, Td,
    Checkbox, Button, HStack, useToast, Spinner, Center, Select,
    Card, CardBody, Badge, Text, Icon, Flex, TableContainer
} from '@chakra-ui/react';
import { FiCheckSquare, FiArrowLeft, FiXCircle, FiRefreshCw } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns'; // Ensure date-fns is installed (usually is in this stack)
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';

const RollCallPage = () => {
    const [students, setStudents] = useState([]);
    const [attendanceMap, setAttendanceMap] = useState(new Map()); // studentID -> status (PRESENT/LEAVE/etc)
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const [allClasses, setAllClasses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [selectedDept, setSelectedDept] = useState('');
    const [selectedClassId, setSelectedClassId] = useState('');

    const FIXED_DEPARTMENTS = [
        { value: 'AfterSchool', label: '全科班' },
        { value: 'English', label: '英文部' },
        { value: 'Talent', label: '才藝部' },
    ];

    const toast = useToast();
    const navigate = useNavigate();
    const { user } = useAuth();

    // Helper: Get Today's Date YYYY-MM-DD
    const getTodayStr = () => format(new Date(), 'yyyy-MM-dd');

    // Fetch Classes on Mount
    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const res = await authAPI.getClasses({ limit: 100 });
                let classesData = res.data;
                if (user?.role === 'teacher') {
                    classesData = filterClassesForStaffUser(classesData, user);
                    if (classesData.length > 0) {
                        const firstClass = classesData[0];
                        if (firstClass.department) {
                            setSelectedDept(firstClass.department);
                            setTimeout(() => setSelectedClassId(firstClass.id.toString()), 100);
                        }
                    }
                }
                setAllClasses(classesData);
                const foundDepts = [...new Set(classesData.map(c => c.department).filter(Boolean))];
                const deptMap = new Map();
                FIXED_DEPARTMENTS.forEach(d => deptMap.set(d.value, d));
                foundDepts.forEach(dVal => {
                    if (!deptMap.has(dVal)) deptMap.set(dVal, { value: dVal, label: dVal });
                });
                setDepartments(Array.from(deptMap.values()));
            } catch (error) {
                console.error("Failed to fetch classes", error);
                toast({ title: "無法載入班級列表", status: "error" });
            }
        };
        fetchClasses();
    }, [user]);

    // Fetch Students AND Attendance when Class Selected
    useEffect(() => {
        if (!selectedClassId) {
            setStudents([]);
            setAttendanceMap(new Map());
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                const todayStr = getTodayStr();

                // Parallel Fetch: Students + Today's Class Attendance
                const [studentsRes, attendanceRes] = await Promise.all([
                    authAPI.getStudents({ limit: 1000, class_id: selectedClassId }),
                    authAPI.attendance.getClassAttendance(selectedClassId, todayStr)
                ]);

                // Filter Students
                let fetchedStudents = studentsRes.data;
                // Filter is handled by Backend (getStudents({ class_id }))
                // Do NOT filter by s.class_id here, otherwise secondary class students are hidden.
                /* 
                if (selectedClassId) {
                    fetchedStudents = fetchedStudents.filter(s => s.class_id === parseInt(selectedClassId) || s.class?.id === parseInt(selectedClassId));
                }
                */
                setStudents(fetchedStudents);

                // Map Attendance: StudentID -> Status
                const newMap = new Map();
                if (attendanceRes.data) {
                    attendanceRes.data.forEach(record => {
                        newMap.set(record.student_id, record.status); // PRESENT, LEAVE, etc.
                    });
                }
                setAttendanceMap(newMap);
                setSelectedIds([]);

            } catch (error) {
                console.error(error);
                toast({ title: "無法取得清單", status: "error" });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedClassId]);

    // Logic: 
    // Left Col (Checkable) = No record in ClassAttendance (AND not Global Leave?)
    // Actually, if Global status is LEAVE, maybe we should auto-mark them?
    // For now, let's stick to strict Class Attendance. If no record -> Left Col.

    const checkableStudents = students.filter(s => {
        // If they have a class attendance record (PRESENT/LEAVE), they are DONE (Right Col).
        // If no record, they are TODO (Left Col).
        // Exception: If Global Status is LEAVE, do we show them? 
        // Plan says: Global Leave implies NO roll call needed.
        // Let's check s.status (Global).
        if (s.status === 'LEAVE') return false; // Global Leave -> Don't call role? Or show in Right col?

        return !attendanceMap.has(s.id);
    });

    // Right Col (Done) = Has Class Attendance OR Global Leave
    const arrivedStudents = students.filter(s => {
        if (s.status === 'LEAVE') return true; // Global Leave -> Always in "Done" list
        return attendanceMap.has(s.id);
    });

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(checkableStudents.map(s => s.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(pid => pid !== id));
        }
    };

    const handleReset = async (studentId) => {
        if (!confirm('確定要重置這位學生的點名狀態嗎？')) return;

        try {
            const todayStr = getTodayStr();
            await authAPI.attendance.batchUpdateClassAttendance(
                parseInt(selectedClassId),
                [studentId],
                todayStr,
                'RESET'
            );

            toast({
                title: "重置成功",
                status: "success",
                duration: 2000,
            });

            // Optimistic Update
            // 1. Remove from Attendance Map
            const newMap = new Map(attendanceMap);
            newMap.delete(studentId);
            setAttendanceMap(newMap);

            // 2. [New] Update Local Student State (to clear Global Leave status)
            // This ensures they move back to the "Checkable" (Left) list immediately
            setStudents(prev => prev.map(s => {
                if (s.id === studentId) {
                    return { ...s, status: 'NOT_ARRIVED' };
                }
                return s;
            }));

        } catch (error) {
            console.error(error);
            toast({ title: "重置失敗", status: "error" });
        }
    };

    const handleSubmit = async (status = 'PRESENT') => {
        if (selectedIds.length === 0) {
            toast({ title: "請至少選擇一位學生", status: "warning" });
            return;
        }

        try {
            setSubmitting(true);
            const todayStr = getTodayStr();
            const statusLabel = status === 'PRESENT' ? '出席' : '請假';

            // Phase 26: Call Batch Class Attendance API
            await authAPI.attendance.batchUpdateClassAttendance(
                parseInt(selectedClassId),
                selectedIds,
                todayStr,
                status
            );

            toast({
                title: "點名完成",
                description: `已成功將 ${selectedIds.length} 位學生標記為${statusLabel} `,
                status: "success",
                duration: 3000,
            });

            // Refetch / Update Local State
            // Optimistic update for speed
            const newMap = new Map(attendanceMap);
            selectedIds.forEach(id => newMap.set(id, status));
            setAttendanceMap(newMap);

            // [Fix] Optimistically update Global Status so "全校狀態" column updates immediately
            setStudents(prev => prev.map(s => {
                if (selectedIds.includes(s.id)) {
                    if (status === 'PRESENT') {
                        // Mirror Backend Logic: Only NOT_ARRIVED -> ARRIVED
                        if (s.status === 'NOT_ARRIVED') {
                            return { ...s, status: 'ARRIVED' };
                        }
                    } else if (status === 'LEAVE') {
                        // Mirror Backend Logic: Always updates to LEAVE
                        return { ...s, status: 'LEAVE' };
                    }
                }
                return s;
            }));

            setSelectedIds([]);

        } catch (error) {
            console.error(error);
            toast({ title: "點名失敗", description: "請檢查網路或稍後再試", status: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    // Filter Logic for Dropdowns
    const availableClasses = selectedDept
        ? allClasses.filter(c => c.department === selectedDept)
        : [];

    return (
        <Flex direction="column" h="100%" overflow="hidden" py={6}>
            {/* Header Section - Fixed */}
            <Box flexShrink={0} mb={2}>


                <HStack justifyContent="space-between" mb={6}>
                    <Heading size="lg" color="brand.800">
                        <Icon as={FiCheckSquare} mr={2} />
                        班級點名
                    </Heading>
                    <HStack spacing={3}>
                        <Button
                            colorScheme="yellow"
                            variant="solid"
                            size="lg"
                            onClick={() => handleSubmit('LEAVE')}
                            isLoading={submitting}
                            isDisabled={selectedIds.length === 0}
                            leftIcon={<Icon as={FiXCircle} />}
                        >
                            標記請假
                        </Button>
                        <Button
                            colorScheme="brand"
                            size="lg"
                            onClick={() => handleSubmit('PRESENT')}
                            isLoading={submitting}
                            isDisabled={selectedIds.length === 0}
                            leftIcon={<Icon as={FiCheckSquare} />}
                        >
                            確認今日出席 ({selectedIds.length})
                        </Button>
                    </HStack>
                </HStack>

                {/* Filter Section (Admin Only) */}
                {user?.role !== 'teacher' ? (
                    // 不覆寫 bg：主題的 Card 已是卡片面 white。壓成 gray.50 會跟行政外殼同色，
                    // 這張篩選卡就整個融進背景、只剩外框。
                    <Card mb={6} variant="outline">
                        <CardBody py={3}>
                            <HStack spacing={4}>
                                <HStack>
                                    <Text fontWeight="bold" whiteSpace="nowrap">部門:</Text>
                                    <Select
                                        placeholder="請選擇部門"
                                        bg="white"
                                        maxW="200px"
                                        value={selectedDept}
                                        onChange={(e) => {
                                            setSelectedDept(e.target.value);
                                            setSelectedClassId('');
                                        }}
                                    >
                                        {departments.map(dept => (
                                            <option key={dept.value} value={dept.value}>{dept.label}</option>
                                        ))}
                                    </Select>
                                </HStack>

                                <HStack>
                                    <Text fontWeight="bold" whiteSpace="nowrap">班級:</Text>
                                    <Select
                                        placeholder="請選擇班級"
                                        bg="white"
                                        maxW="200px"
                                        isDisabled={!selectedDept}
                                        value={selectedClassId}
                                        onChange={(e) => setSelectedClassId(e.target.value)}
                                    >
                                        {availableClasses.map(cls => (
                                            <option key={cls.id} value={cls.id}>{cls.name}</option>
                                        ))}
                                    </Select>
                                </HStack>
                            </HStack>
                        </CardBody>
                    </Card>
                ) : (
                    /* Smart Layout for Teachers: 
                       - If multiple classes -> Show Dropdown
                       - If single class -> Show Read-Only Card (Cleaner)
                    */
                    allClasses.length > 1 ? (
                        <Card mb={6} variant="outline" bg="blue.50" borderColor="blue.200">
                            <CardBody py={3}>
                                <HStack>
                                    <Icon as={FiCheckSquare} color="blue.600" boxSize={6} />
                                    <Text fontWeight="bold" whiteSpace="nowrap" color="blue.800">
                                        切換點名班級:
                                    </Text>
                                    <Select
                                        bg="white"
                                        maxW="300px"
                                        value={selectedClassId}
                                        onChange={(e) => setSelectedClassId(e.target.value)}
                                        borderColor="blue.300"
                                    >
                                        {allClasses.map(cls => (
                                            <option key={cls.id} value={cls.id}>{cls.name}</option>
                                        ))}
                                    </Select>
                                </HStack>
                            </CardBody>
                        </Card>
                    ) : (
                        <Card mb={6} bg="blue.50" borderLeftWidth="4px" borderColor="blue.500">
                            <CardBody py={4}>
                                <HStack>
                                    <Icon as={FiCheckSquare} color="blue.600" boxSize={6} />
                                    <Box>
                                        <Text fontSize="sm" color="blue.600" fontWeight="bold">目前點名班級</Text>
                                        <Heading size="md" color="blue.800">
                                            {allClasses.find(c => c.id.toString() === selectedClassId)?.name || '正在載入班級...'}
                                        </Heading>
                                    </Box>
                                </HStack>
                            </CardBody>
                        </Card>
                    )
                )}
            </Box>

            {/* Flexible Content Section - Split View on Desktop */}
            <Flex flex="1" overflow="hidden" direction={{ base: 'column', lg: 'row' }} gap={6} px={1} pb={4}>

                {/* Left Column: Unmarked (Actionable) */}
                <Box
                    flex={1}
                    overflowY="auto"
                    css={{
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                    }}
                >
                    {loading ? (
                        <Center h="200px"><Spinner /></Center>
                    ) : !selectedClassId ? (
                        // 空狀態面板改用卡片面 white：行政外殼本身就是 neutral.50（=gray.50），
                        // 原本的 gray.50 跟底色一模一樣、又沒有邊框，整個面板等於沒畫出來。
                        <Center h="300px" flexDirection="column" bg="white" borderRadius="md" boxShadow="card">
                            <Icon as={FiCheckSquare} boxSize={12} color="gray.300" mb={4} />
                            <Text color="gray.500" fontSize="lg">請先選擇部門與班級</Text>
                            <Text color="gray.400">選擇後將顯示該班級學生清單</Text>
                        </Center>
                    ) : (
                        <Card minH="50vh" shadow="sm">
                            <CardBody>
                                <HStack mb={4} justifyContent="space-between">
                                    <Checkbox
                                        isChecked={checkableStudents.length > 0 && selectedIds.length === checkableStudents.length}
                                        isIndeterminate={selectedIds.length > 0 && selectedIds.length < checkableStudents.length}
                                        onChange={handleSelectAll}
                                        size="lg"
                                        colorScheme="brand"
                                    >
                                        全選今日出席
                                    </Checkbox>
                                    <Text color="gray.500" fontSize="sm">
                                        未點名: {checkableStudents.length} 人
                                    </Text>
                                </HStack>

                                <TableContainer w="100%" maxW={{ base: 'calc(100vw - 64px)', md: '100%' }} overflowX="auto">
                                    <Table variant="simple" size="sm" whiteSpace="nowrap">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th width="50px"></Th>
                                                <Th>學生姓名</Th>
                                                <Th>班級</Th>
                                                <Th>全校狀態</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {checkableStudents.map(student => (
                                                <Tr key={student.id} _hover={{ bg: 'gray.50' }}>
                                                    <Td>
                                                        <Checkbox
                                                            isChecked={selectedIds.includes(student.id)}
                                                            onChange={(e) => handleSelectOne(student.id, e.target.checked)}
                                                            size="lg"
                                                            colorScheme="brand"
                                                        />
                                                    </Td>
                                                    <Td fontWeight="bold" fontSize="md">{student.full_name}</Td>
                                                    <Td>{student.class?.name}</Td>
                                                    <Td>
                                                        <Badge colorScheme={student.status === 'ARRIVED' ? 'green' : 'gray'}>
                                                            {student.status === 'ARRIVED' ? '已在校' : '未到校'}
                                                        </Badge>
                                                    </Td>
                                                </Tr>
                                            ))}
                                            {checkableStudents.length === 0 && (
                                                <Tr>
                                                    <Td colSpan={4} textAlign="center" py={8} color="green.500">
                                                        本班所有學生皆已點名
                                                    </Td>
                                                </Tr>
                                            )}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </CardBody>
                        </Card>
                    )}
                </Box>

                {/* Right Column: Arrived & Leave (Read-only) + Class Attendance Status */}
                <Box
                    flex={1}
                    overflowY="auto"
                    display={!selectedClassId ? 'none' : 'block'}
                    css={{
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                    }}
                >
                    <Heading size="md" mb={4} color="gray.600">已點名 / 請假清單</Heading>
                    <Card shadow="sm">
                        <CardBody>
                            <TableContainer>
                                <Table size="sm" variant="simple" whiteSpace="nowrap">
                                    <Thead bg="gray.50">
                                        <Tr>
                                            <Th>姓名</Th>
                                            <Th>班級出席</Th>
                                            <Th>全校狀態</Th>
                                            <Th>操作</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {arrivedStudents.map(s => {
                                            const classStatus = attendanceMap.get(s.id) || (s.status === 'LEAVE' ? 'LEAVE' : 'UNKNOWN');
                                            return (
                                                <Tr key={s.id}>
                                                    <Td fontSize="md">{s.full_name}</Td>
                                                    <Td>
                                                        <Badge
                                                            colorScheme={
                                                                classStatus === 'PRESENT' ? 'green' :
                                                                    classStatus === 'LEAVE' ? 'purple' :
                                                                        'gray'
                                                            }
                                                        >
                                                            {classStatus === 'PRESENT' ? '已出席' :
                                                                classStatus === 'LEAVE' ? '請假' : classStatus}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Badge
                                                            variant="outline"
                                                            colorScheme={
                                                                s.status === 'ARRIVED' ? 'green' :
                                                                    s.status === 'LEAVE' ? 'purple' :
                                                                        'gray'
                                                            }
                                                        >
                                                            {s.status === 'ARRIVED' ? '已在校' :
                                                                s.status === 'LEAVE' ? '請假' : '未到校'}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => handleReset(s.id)}
                                                            title="重置為未點名"
                                                        >
                                                            <Icon as={FiRefreshCw} />
                                                        </Button>
                                                    </Td>
                                                </Tr>
                                            )
                                        })}
                                        {arrivedStudents.length === 0 && (
                                            <Tr>
                                                <Td colSpan={4} textAlign="center" color="gray.400" py={4}>尚無已點名資料</Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </CardBody>
                    </Card>
                </Box>
            </Flex>
        </Flex>
    );
};

export default RollCallPage;
